import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { REQUIRED_FIELDS_MISSING_MESSAGE } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { AuthService } from '@services/auth.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { UserService } from '@services/user.service';
import { of, Subject } from 'rxjs';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'gc-contact-details',
  templateUrl: './contact-details.component.html',
  styleUrls: ['./contact-details.component.scss'],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          maxHeight: '100%',
          opacity: 1,
          overflow: 'hidden',
        }),
      ),
      state(
        'closed',
        style({
          height: 0,
          opacity: 0.5,
          overflow: 'hidden',
        }),
      ),
      transition('open <=> closed', [animate('0.4s')]),
    ]),
  ],
  standalone: false,
})
export class ContactDetailsComponent implements OnInit, OnDestroy {
  @Input() editingDependant: boolean;
  @Input() contactDetailsForm: FormGroup;
  @Input() editingUserAccount = false;
  otpControl: FormControl;
  isOpen = false;
  mobileNumberVerified = false;
  currentNumber: string;
  otpLength = 4;
  private readonly unsubscribe$ = new Subject<void>();
  CELLNR_REGEX: RegExp;
  constructor(
    private readonly alertService: AlertService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly configService: ConfigLocaleService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.otpControl = new FormControl('', [Validators.required]);

    if (this.editingUserAccount) {
      this.userService.profile$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
        this.mobileNumberVerified = user.cellnrVerified;
        this.currentNumber = user.cellnr;
      });
      this.contactDetailsForm.get('cellnr').valueChanges.subscribe((cellnr) => {
        if (this.currentNumber === cellnr) {
          this.mobileNumberVerified = true;
          this.contactDetailsForm.get('cellnr').markAsPristine();
          this.isOpen = false;
          this.otpControl.setValue('');
        } else {
          this.mobileNumberVerified = false;
        }
      });
      this.otpControl.valueChanges
        .pipe(
          tap((value) => {
            if (value?.length === this.otpLength) {
              this.verifyOTP().subscribe(
                (res) => {
                  if (res) {
                    this.otpControl.setValue('');
                    this.isOpen = !this.isOpen;
                    this.alertService.snackbar('mobile_number_updated', 'dismiss');
                    this.mobileNumberVerified = true;
                    this.currentNumber = this.contactDetailsForm.get('cellnr').value;
                    this.contactDetailsForm.get('cellnr').markAsPristine();
                  }
                },
                (err: unknown) => {
                  this.alertService.handleErrorDialog$(err, this.translateService.instant('error_verifying_otp'));
                  this.otpControl.setValue('');
                },
              );
            }
          }),
        )
        .subscribe();
    }

    const cellValidators = this.configService.$cellValidatorsJson();
    if (cellValidators && Object.keys(cellValidators).length > 0) {
      this.CELLNR_REGEX = new RegExp(cellValidators.pattern);
    }
  }

  get disableVerify() {
    return this.mobileNumberVerified || !this.contactDetailsForm.get('cellnr').value.match(this.CELLNR_REGEX);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }

  onSubmit() {
    if (this.contactDetailsForm.valid) {
      if (!this.mobileNumberVerified) {
        this.alertService.snackbar('please_verify_your_mobile_number');
      } else {
        const contactDetails = this.contactDetailsForm.value;
        this.userService.updateUserProfile(contactDetails).subscribe(
          () => {
            this.alertService.snackbar('contact_details_updated');
            this.contactDetailsForm.markAsPristine();
          },
          (err: unknown) => {
            this.alertService.handleErrorDialog$(err, this.translateService.instant('error_updating_your_details'));
          },
        );
      }
    } else {
      this.alertService.snackbar(REQUIRED_FIELDS_MISSING_MESSAGE);
      this.contactDetailsForm.markAllAsTouched();
    }
  }

  changeEmail() {
    if (this.editingUserAccount) {
      const data = {
        title: this.translateService.instant('change_email_address'),
        tag: 'question',
        submitText: this.translateService.instant('change'),
        cancelText: this.translateService.instant('cancel'),
        questions: [
          {
            key: 'email',
            label: this.translateService.instant('email_address'),
            required: true,
            type: 'email',
          },
        ],
      };
      const config = { hasBackdrop: true, maxWidth: 300 };
      this.alertService
        .inputDialog$(data, config)
        .pipe(
          filter((d: any) => !!d?.email?.trim()),
          tap(({ email }) => {
            const oldEmail = this.contactDetailsForm.get('email').value;
            this.authService
              .changeEmail(oldEmail, email)
              .pipe(
                catchError((err: unknown) => {
                  this.alertService.handleErrorDialog$(err, this.translateService.instant('error_updating_your_details'));
                  return of({});
                }),
                filter((success) => !!success),
              )
              .subscribe(() => {
                this.alertService.snackbar('email_request_sent', 'dismiss');
              });
          }),
        )
        .subscribe();
    }
  }

  verifyOTP() {
    const otp = this.otpControl.value.toUpperCase();
    return this.authService.verifyMobile(otp).pipe(
      tap((res) => {
        if (res) {
          this.userService.getUserProfile().subscribe();
        }
      }),
    );
  }

  sendOtp() {
    const { cellnr } = this.contactDetailsForm.value;
    this.authService
      .requestMobileVerification$(cellnr)
      .pipe(map((res) => !!res.success))
      .subscribe(
        (res) => {
          if (res) {
            this.alertService.snackbar('otp_has_been_sent', 'dismiss');
            this.isOpen = true;
          }
        },
        (err: unknown) => {
          this.alertService.handleErrorDialog$(err, this.translateService.instant('error_requesting_otp'));
        },
      );
  }

  verifyMobile() {
    if (this.isOpen) {
      // If the OTP section is expanded, close it
      this.isOpen = false;
    } else {
      this.sendOtp();
    }
  }
}
